import "./SessionTabs.css";
import { agoText, runningText } from "../chat/api";
import type { Session } from "../chat/api";
import { tx } from "../i18n";

/* ══ 세션 탭 ══
   단계 띠에서 고른 칸의 세션들이 한 줄로 선다(안 B). 맨 앞은 늘 「◇ 발자국」 — 세션 행이
   없는 **사람 세션(Roo Code)** 의 자리다. 이 이슈의 이력(`GET /tasks/:id/events`)이 곧
   그 세션의 대화라, 어느 단계를 골라도 같은 탭이 선다.

   표식은 세션 기록의 사실이다 — 열림(●) · `outcome`(✓ ok · ■ stopped · ✕ 그 밖) · 사람(◇). */

export const FOOTPRINT_TAB = "__footprints__";

export function sessionGlyph(s: Session): { glyph: string; word: string; tone: string } {
  if (s.endedAt === null) {
    if (s.stopRequestedAt) return { glyph: "■", word: tx("chat.stopping"), tone: "stopping" };
    return { glyph: "●", word: tx("issue.working"), tone: "running" };
  }
  if (s.outcome === "ok") return { glyph: "✓", word: tx("chat.ended"), tone: "ok" };
  if (s.outcome === "stopped") return { glyph: "■", word: tx("chat.stop"), tone: "bad" };
  return { glyph: "✕", word: s.outcome ?? tx("chat.endUnknown"), tone: "bad" };
}

/** 탭 한 줄의 짧은 말 — 「naru-1 · claude · 12분째」 / 「naru-1 · 8분 · 3시간 전」. */
export function sessionMeta(s: Session, now = Date.now()): string {
  const who = [s.runner, s.vendor].filter(Boolean).join(" · ");
  if (s.endedAt === null) return `${who} · ${runningText(s.startedAt, now)}`;
  return `${who} · ${agoText(s.endedAt, now)}`;
}

export function SessionTabs({
  sessions,
  selected,
  onSelect,
  footprintCount,
}: {
  /** 이 단계의 세션들 — 오래된 것부터(#1 이 첫 시도). */
  sessions: Session[];
  selected: string;
  onSelect: (id: string) => void;
  /** 발자국 수. **null 은 아직 안 읽은 것** — 0 과 다르다. */
  footprintCount: number | null;
}) {
  return (
    <div className="stabs" role="tablist" aria-label={tx("chat.sessions")}>
      <button
        type="button"
        role="tab"
        className={`b-chip stab stab--human${selected === FOOTPRINT_TAB ? " is-on" : ""}`}
        aria-selected={selected === FOOTPRINT_TAB}
        title={tx("chat.footprintTip")}
        onClick={() => onSelect(FOOTPRINT_TAB)}
      >
        <span className="stab__glyph" aria-hidden="true">
          ◇
        </span>
        {tx("chat.footprint")}{footprintCount === null ? "" : ` ${footprintCount}`}
      </button>
      {sessions.map((s, i) => {
        const g = sessionGlyph(s);
        return (
          <button
            type="button"
            role="tab"
            key={s.id}
            className={`b-chip stab stab--${g.tone}${selected === s.id ? " is-on" : ""}`}
            aria-selected={selected === s.id}
            aria-label={`#${i + 1} · ${g.word} · ${sessionMeta(s)}`}
            title={`${g.word} · ${sessionMeta(s)}${s.note ? ` — ${s.note}` : ""}`}
            onClick={() => onSelect(s.id)}
          >
            <span className="stab__glyph" aria-hidden="true">
              {g.glyph}
            </span>
            #{i + 1}
            <span className="stab__meta">{s.runner}</span>
          </button>
        );
      })}
      {sessions.length === 0 ? <span className="stab__none">{tx("chat.noStageSession")}</span> : null}
    </div>
  );
}
